import React, { useState, useEffect } from 'react';
import qs from 'qs';
import {
  Typography,
  Button,
  InputBase,
  InputLabel,
  Link,
  Box,
} from '@mui/material';
import api from '~/_base/constants/api';
import { SSOProvider } from '~/models';
import { useToast } from '~/_base/context/toastContext';
import logger from '~/_base/logger';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

export default function SignIn() {
  const { errorToast } = useToast();
  const queryString = (document.location.search || '').slice(1);
  const params = qs.parse(queryString);
  const [email, setEmail] = useState(params['email'] || '');
  const [password, setPassword] = useState('');
  const [ssoProviders, setSsoProviders] = useState<SSOProvider[]>([]);
  const { i18n, t } = useTranslation();

  useEffect(() => {
    async function getSSOProviders() {
      try {
        const json = await api.ssoProviders.request();
        setSsoProviders(json.providers);
      } catch (err) {
        errorToast(
          `An error occured while fetching SSO providers: ${(err as Error).message}`
        );
      }
    }

    getSSOProviders();
  }, [errorToast]);

  useEffect(() => {
    const lang = document.documentElement.lang;
    i18n.changeLanguage(lang);
    moment.locale(lang);
  }, [i18n]);

  async function handleSigninClick() {
    logger.info('Sign in button clicked');
    try {
      const json = await api.signin.request(
        JSON.stringify({
          session: {
            email,
            password,
          },
        })
      );
      if (json.location) {
        sessionStorage.setItem('auth_method', 'Native');
        document.location.href = json.location;
      }
    } catch (err) {
      errorToast((err as Error).message);
    }
  }

  function handleKeyPress(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      handleSigninClick();
    }
  }

  return (
    <React.Fragment>
      {ssoProviders.length > 0 ? (
        <Box maxWidth="328px" mx="auto">
          <Box display="flex" flexDirection="column" alignItems="center">
            {ssoProviders.map((item, index) => (
              <a
                key={index}
                href={item.path}
                style={{ width: '100%', margin: '0 0 16px 0', padding: 0, textDecoration: 'none' }}
                data-method={item?.method || 'get'}
                onClick={() => {
                  try {
                    sessionStorage.setItem('auth_method', 'SSO');
                  } catch (e) {
                    console.error(e);
                  }
                }}
              >
                <Button variant="contained" color="secondary" sx={{ m: 0, p: 0, width: '100%', display: 'flex', alignItems: 'center', position: 'relative' }}>
                  <img
                    src={item.logo}
                    alt={`sso button for ${item.name}`}
                    style={{ height: '40px', width: '40px' }}
                  />
                  <Typography variant="body1" sx={{ width: '100%' }}>
                    {t('Sign in with {{name}}', { name: item.name })}
                  </Typography>
                </Button>
              </a>
            ))}
          </Box>

          <Box position="relative" zIndex={0} display="flex" justifyContent="center" my="15px">
            <Box position="absolute" width="100%" top="8px" left={0} zIndex={-1} borderBottom="solid 1px #e6e6e6" />
            <Box color="#6f6f6f" textAlign="center" fontStyle="italic" width="36px" bgcolor="#fff" zIndex={1} fontSize="12px">{t('or')}</Box>
          </Box>
        </Box>
      ) : null}
      <Box maxWidth="328px" mx="auto" mt="1.5rem">
        <InputLabel htmlFor="session_email">{t('Email')}</InputLabel>
        <InputBase
          onKeyPress={handleKeyPress}
          id="session_email"
          name="session[email]"
          value={email}
          fullWidth
          type="email"
          onChange={e => setEmail(e.currentTarget.value)}
          placeholder={t('Email Address')}
          autoComplete="off"
        />
      </Box>
      <Box maxWidth="328px" mx="auto" mt="1.5rem">
        <InputLabel htmlFor="session_password">{t('Password')}</InputLabel>
        <InputBase
          onKeyPress={handleKeyPress}
          id="session_password"
          value={password}
          onChange={e => setPassword(e.currentTarget.value)}
          name="session[password]"
          type="password"
          fullWidth
          placeholder={t('Password')}
          autoComplete="off"
        />
      </Box>
      <Box maxWidth="328px" mx="auto" mt="1.5rem">
        <Button
          sx={{ minWidth: '100%' }}
          variant="contained"
          color="primary"
          id="apeSignInBtn"
          onClick={() => handleSigninClick()}
        >
          {t('Sign in')}
        </Button>
      </Box>
      <Box maxWidth="328px" mt="15px" mx="auto" textAlign="center">
        <Link
          onClick={() => logger.info('Forgotten password link clicked')}
          href="/password_resets/new"
          sx={{ fontStyle: 'italic' }}>
          {t('Forgotten password?')}
        </Link>
        <Typography variant="body1" sx={{ mt: 5, pt: 4, borderTop: '1px solid #e6eaec' }}>
          {t(
            'New user? Ask your systems administrator or project manager to create your account.'
          )}
        </Typography>
      </Box>
    </React.Fragment>
  );
}
